import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subscription, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PDFPagePreview } from '../pdf-preview-selector/pdf-preview-selector.component';
import {
  PdfManagerService,
  PdfProcessingState,
} from '../../../pdf-canvas-item/pdf-services/pdf-manager.service';

@UntilDestroy()
@Component({
  selector: 'app-new-load-pdf-and-preview[contentId][fileName][fileSize]',
  templateUrl: './new-load-pdf-and-preview.component.html',
  styleUrls: ['./new-load-pdf-and-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewLoadPdfAndPreviewComponent implements AfterViewInit, OnInit {
  @Input() contentId!: string;
  @Input() fileName!: string;
  @Input() fileSize!: number;
  @Output() submitSelectedIndices = new EventEmitter<number[]>();
  @Output() cancelSubmit = new EventEmitter<void>();

  isLoading$ = new BehaviorSubject<boolean>(true);
  uploadProgress$!: Observable<number>;
  processingState$!: Observable<PdfProcessingState>;
  pageItems$ = new BehaviorSubject<PDFPagePreview[]>([]);

  loadingStateSubscription?: Subscription;
  protected readonly PdfProcessingState = PdfProcessingState;

  constructor(private pdfManagerService: PdfManagerService) {}

  ngOnInit() {
    this.uploadProgress$ = this.pdfManagerService.getPdfUploadProgress(this.contentId);
    this.processingState$ = this.pdfManagerService.getPdfProcessingState(this.contentId);
  }

  ngAfterViewInit() {
    this.loadingStateSubscription = this.pdfManagerService
      .getPdfLoadingState(this.contentId)
      .pipe(untilDestroyed(this))
      .subscribe(async (isLoading) => {
        this.isLoading$.next(isLoading);

        if (!isLoading && this.loadingStateSubscription) {
          this.loadingStateSubscription?.unsubscribe();
          this.loadingStateSubscription = undefined;

          this.emitPagesPreviews();
        }
      });
  }

  private async emitPagesPreviews() {
    const conversionResponse = await this.pdfManagerService.getConvertedImages(this.contentId);
    if (!conversionResponse) {
      return;
    }
    const decompressedImagesSizes =
      this.pdfManagerService.decompressImagesSizes(conversionResponse);

    const previewPages = decompressedImagesSizes.map((p, i) => ({
      isSelected: false,
      imageUrl: this.pdfManagerService.getImageUrl(
        i,
        conversionResponse.imagesPrefix,
        conversionResponse.imagesToken,
      ),
      width: decompressedImagesSizes[i].width,
      height: decompressedImagesSizes[i].height,
    }));

    this.pageItems$.next(previewPages ?? []);
  }

  selectedIndicesSubmitted(selectedIndices: number[]) {
    this.submitSelectedIndices.emit(selectedIndices);
  }

  cancelClicked() {
    this.cancelSubmit.emit();
  }
}
